import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { debounceTime, filter, map, startWith, switchMap } from 'rxjs/operators';
import { JobSitesService } from 'src/app/lmo/services/job-sites.service';
import { Circle } from 'src/proto/geo/geo_pb';
import { SmartDropdownComponent } from '~common/smart-dropdown/smart-dropdown.component';
import { LMOSiteSummary, Site, SiteShort } from '~proto/site/site_pb';
import { crowFliesDistance } from '~utilities/distance';
import { idArrayToRecord } from '~utilities/idArrayToRecord';
import { trackById } from '~utilities/trackById';

interface SiteGroup {
  name: string;
  sitesList: LMOSiteSummary.AsObject[] | SiteShort.AsObject[];
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ct-order-details-edit-site-selector',
  styleUrls: ['./order-details-edit-site-selector.component.scss'],
  templateUrl: './order-details-edit-site-selector.component.html',
})
export class OrderDetailsEditSiteSelectorComponent implements OnInit {
  public trackById = trackById;
  @Output() public selected = new EventEmitter<{ pickupFrom: LMOSiteSummary.AsObject | SiteShort.AsObject }>();
  @Input() public site: Site.AsObject;
  @ViewChild('smartDropdown', { static: true }) private smartDropdown: SmartDropdownComponent;
  private topResult: LMOSiteSummary.AsObject | SiteShort.AsObject;
  public searching$: Observable<boolean>;
  public textChanges$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public sites$: Observable<(LMOSiteSummary.AsObject | SiteShort.AsObject)[]>;
  public sitesWithHeaders$: Observable<SiteGroup[]>;
  public siteGeofence$: Observable<Circle.AsObject>;

  constructor(private sites: JobSitesService) {}

  public ngOnInit() {
    this.searching$ = this.sites.searching$;
    this.siteGeofence$ = this.sites.currentSite$.pipe(
      filter((site) => !!site),
      map((site) => site.geofence),
    );
    const searchResults$ = combineLatest([
      this.sites.sites$,
      this.textChanges$$.pipe(debounceTime(200)).pipe(
        switchMap((searchText) => {
          if (searchText && searchText.length >= 2) {
            return this.sites.searchForSite$(searchText);
          }
          return of([] as SiteShort.AsObject[]);
        }),
      ),
    ]).pipe(
      map(([sites, searchResults]) => {
        if (!sites) {
          return searchResults;
        }
        const siteMap = idArrayToRecord(sites);
        return searchResults.filter((site) => !site.archived && !siteMap[site.id]);
      }),
    );
    this.sitesWithHeaders$ = combineLatest([
      this.sites.sites$,
      this.sites.currentSite$,
      this.textChanges$$.pipe(
        debounceTime(100),
        startWith(''),
      ),
      searchResults$,
    ]).pipe(
      map(([sites, currentSite, text, searchResults]) => {
        if (!sites) {
          return [];
        }
        if (!currentSite) {
          return [];
        }
        const withoutCurrentSite = sites.filter((site) => site.id !== currentSite.id);
        const pinned: LMOSiteSummary.AsObject[] = [];
        const recent: LMOSiteSummary.AsObject[] = [];
        withoutCurrentSite.forEach((site) => {
          if (site.favorited) {
            pinned.push(site);
          } else {
            recent.push(site);
          }
        });
        const sitesWithHeaders = [
          {
            name: 'Pinned',
            sitesList: pinned,
          },
          {
            name: 'Recent',
            sitesList: recent,
          },
        ] as SiteGroup[];
        if (!text || text === '') {
          this.topResult = withoutCurrentSite.length ? withoutCurrentSite[0] : null;
          return sitesWithHeaders;
        }
        const asLowercase = text.toLocaleLowerCase();
        const cleanedItems = sitesWithHeaders.map((item) => {
          const filteredSites = (item.sitesList as (LMOSiteSummary.AsObject | SiteShort.AsObject)[]).filter((site) =>
            site.name.toLocaleLowerCase().includes(asLowercase),
          );
          return {
            ...item,
            sitesList: filteredSites,
          };
        });
        const mergedGroups = [
          ...cleanedItems,
          {
            name: 'Search Results',
            sitesList: searchResults,
          },
        ] as SiteGroup[];
        const filtered: SiteGroup[] = mergedGroups.filter((item) => item.sitesList.length > 0);
        if (filtered.length && filtered[0].sitesList.length) {
          this.topResult = filtered[0].sitesList[0];
        } else {
          this.topResult = null;
        }
        return filtered;
      }),
    );
  }

  public togglePin(event: Event, siteID: number, isFavorited: boolean) {
    event.stopPropagation();
    this.sites.setFavorite(isFavorited, siteID);
  }

  public inputTextChange(event: string) {
    this.textChanges$$.next(event.trim());
  }

  // calculate as the crow flies distance
  public distance(lat1: number, lon1: number, lat2: number, lon2: number) {
    return crowFliesDistance(lat1, lon1, lat2, lon2);
  }

  public selectSite(pickupFrom: LMOSiteSummary.AsObject | SiteShort.AsObject) {
    this.selected.emit({ pickupFrom });
    this.smartDropdown.close();
  }

  public enterKeyPressed() {
    if (this.topResult) {
      this.selectSite(this.topResult);
    } else {
      // this.createNewSite();
    }
  }
}
